import Decimal from 'decimal.js';
import { BaseService } from '@/lib/services/BaseService';
import { OrderBookData } from '@/lib/types/order-book';
import { invertOrderBook } from '@/lib/utils/invertOrderBook';

export type UIOrderBookData = {
  marketId: string;
  yes: OrderBookData;
  no: OrderBookData;
};

type OrderBookDataCb = (data: UIOrderBookData | null) => void;

export class OrderBookService extends BaseService {
  readonly serviceName = 'OrderBookService';
  private yesBookData: OrderBookData | undefined;
  private _noBookData: OrderBookData | undefined; // Cached inverted book
  private _yesBookDataDirty = false; // Flag to track if yesBookData changed
  private callbacks: OrderBookDataCb[] = [];
  private initialised = false;

  private data: UIOrderBookData | null = null;
  // Index price levels by priceKey for O(1) access: Map<priceKey, index in array>
  private asksPriceLevelIndex: Map<string, number> = new Map();
  private bidsPriceLevelIndex: Map<string, number> = new Map();

  // Lazy getter for noBookData - only recalculates when yesBookData changed
  private get noBookData(): OrderBookData {
    if (!this.yesBookData) {
      throw new Error('yesBookData must be initialized before accessing noBookData');
    }
    // Recalculate only if yesBookData changed since last access
    if (this._yesBookDataDirty || !this._noBookData) {
      this._noBookData = invertOrderBook(this.yesBookData);
      this._yesBookDataDirty = false;
    }
    return this._noBookData;
  }

  constructor(
    public readonly marketId: string,
    data?: OrderBookData,
  ) {
    super();
    if (data) {
      this.initialise(data);
    }
  }

  getOrderBookData(): UIOrderBookData | null {
    if (!this.initialised) {
      return null;
    }
    return this.data ?? null;
  }

  private notifySubs(): void {
    this.callbacks.forEach((cb) => {
      cb(this.data);
    });
  }

  // Build price level index from array
  private buildPriceLevelIndex(
    arr: OrderBookData['asks'] | OrderBookData['bids'],
  ): Map<string, number> {
    const index = new Map<string, number>();
    for (let i = 0; i < arr.length; i++) {
      const priceKey = String(arr[i].price);
      index.set(priceKey, i);
    }
    return index;
  }

  private updateBooks(data: OrderBookData) {
    const ascAsks = data.asks.toSorted((a, b) => {
      return a.price.lt(b.price) ? -1 : 1;
    });
    const descBids = data.bids.toSorted((a, b) => {
      return a.price.gt(b.price) ? -1 : 1;
    });
    this.yesBookData = {
      ...data,
      asks: ascAsks,
      bids: descBids,
    };
    // Rebuild price level indices after sorting
    this.asksPriceLevelIndex = this.buildPriceLevelIndex(ascAsks);
    this.bidsPriceLevelIndex = this.buildPriceLevelIndex(descBids);
    // Mark yesBookData as dirty - noBookData will be recalculated lazily when accessed
    this._yesBookDataDirty = true;
    // Use getter to ensure noBookData is calculated (lazy evaluation)
    this.data = {
      marketId: this.marketId,
      yes: this.yesBookData!,
      no: this.noBookData, // Getter will recalculate if needed
    };
    this.notifySubs();
  }

  initialise(data: OrderBookData) {
    this.asksPriceLevelIndex.clear();
    this.bidsPriceLevelIndex.clear();
    this.updateBooks(data);
    this.initialised = true;
  }

  // Initialize with order book snapshot from ORDER_BOOK_DATA (full snapshot)
  initializeFromSnapshot(data: { b?: Record<string, number>; s?: Record<string, number> }) {
    this.asksPriceLevelIndex.clear();
    this.bidsPriceLevelIndex.clear();

    // Convert Map structure to price level Map
    const priceToQtyBids = new Map<string, Decimal>();
    const priceToQtyAsks = new Map<string, Decimal>();

    // Process buy orders (bids) - defensive check for undefined/null
    const buyOrders = data?.b ?? {};
    for (const [price, size] of Object.entries(buyOrders)) {
      const sizeDecimal = new Decimal(size);
      if (sizeDecimal.isZero()) continue;

      const priceKey = String(price);
      if (sizeDecimal.gt(0)) {
        priceToQtyBids.set(priceKey, sizeDecimal);
      }
    }

    // Process sell orders (asks) - defensive check for undefined/null
    const sellOrders = data?.s ?? {};
    for (const [price, size] of Object.entries(sellOrders)) {
      const sizeDecimal = new Decimal(size);
      if (sizeDecimal.isZero()) continue;

      const priceKey = String(price);
      if (sizeDecimal.gt(0)) {
        priceToQtyAsks.set(priceKey, sizeDecimal);
      }
    }

    const bids: OrderBookData['bids'] = Array.from(priceToQtyBids.entries()).map(
      ([price, size]) => ({
        price: new Decimal(price),
        size,
      }),
    );

    const asks: OrderBookData['asks'] = Array.from(priceToQtyAsks.entries()).map(
      ([price, size]) => ({
        price: new Decimal(price),
        size,
      }),
    );

    const orderBookData: OrderBookData = {
      marketId: this.marketId,
      bids,
      asks,
    };

    this.updateBooks(orderBookData);
    this.initialised = true;
  }

  // Apply incremental order book delta from ORDER_BOOK_DATA_DELTA
  // Logic: delta contains only changed price levels
  // - size = 0 → remove this price level
  // - size > 0 and exists → replace size (not add delta)
  // - size > 0 and doesn't exist → add new price level
  applyOrderBookDelta(data: { b?: Record<string, number>; s?: Record<string, number> }) {
    if (!this.initialised || !this.yesBookData) {
      console.warn('[OrderBookService] Cannot apply delta: not initialized');
      return;
    }

    // Process buy orders (bids) deltas
    const buyOrders = data?.b ?? {};
    for (const [priceStr, size] of Object.entries(buyOrders)) {
      this.applyPriceLevelDelta('bids', priceStr, size);
    }

    // Process sell orders (asks) deltas
    const sellOrders = data?.s ?? {};
    for (const [priceStr, size] of Object.entries(sellOrders)) {
      this.applyPriceLevelDelta('asks', priceStr, size);
    }

    // Re-sort and notify after all deltas applied
    const orderBookData: OrderBookData = {
      marketId: this.marketId,
      asks: this.yesBookData.asks,
      bids: this.yesBookData.bids,
    };
    this.updateBooks(orderBookData);
  }

  // Apply delta to a single price level
  private applyPriceLevelDelta(side: 'bids' | 'asks', priceStr: string, size: number) {
    if (!this.yesBookData) return;

    const arr = side === 'asks' ? this.yesBookData.asks : this.yesBookData.bids;
    const priceLevelIndex = side === 'asks' ? this.asksPriceLevelIndex : this.bidsPriceLevelIndex;
    const priceKey = String(priceStr);
    const price = new Decimal(priceStr);
    const sizeDecimal = new Decimal(size);

    const existingIdx = priceLevelIndex.get(priceKey);

    if (sizeDecimal.isZero()) {
      // size = 0 → remove this price level
      if (existingIdx !== undefined) {
        arr.splice(existingIdx, 1);
        priceLevelIndex.delete(priceKey);
        // Update indices for all elements after removed index
        for (let i = existingIdx; i < arr.length; i++) {
          const key = String(arr[i].price);
          priceLevelIndex.set(key, i);
        }
      }
    } else if (existingIdx !== undefined) {
      // size > 0 and price exists → replace size (not add delta)
      arr[existingIdx] = { price, size: sizeDecimal };
    } else {
      // size > 0 and price doesn't exist → add new price level
      arr.push({ price, size: sizeDecimal });
      priceLevelIndex.set(priceKey, arr.length - 1);
    }
  }

  subscribe(cb: OrderBookDataCb) {
    const alreadySubscribed = this.callbacks.includes(cb);

    // Avoid duplicate subscriptions for the same callback
    if (!alreadySubscribed) {
      this.callbacks.push(cb);
    }

    // If already initialized, immediately notify new subscriber
    if (this.initialised && this.data) {
      cb(this.data);
    }
  }

  unsubscribe(cb: OrderBookDataCb) {
    this.callbacks = this.callbacks.filter((cur) => cur !== cb);
  }

  unsubscribeAll() {
    this.callbacks = [];
  }

  clearData() {
    this.yesBookData = undefined;
    this._noBookData = undefined;
    this._yesBookDataDirty = false;
    this.data = null;
    this.asksPriceLevelIndex.clear();
    this.bidsPriceLevelIndex.clear();
    this.initialised = false;
    this.notifySubs();
  }

  reset() {
    this.unsubscribeAll();
    this.clearData();
  }
}
